import type { ReactNode } from 'react';
import { PermissionGate } from '../auth/PermissionGate';
import { Tooltip } from './Tooltip';

export type AlertTone = 'blue' | 'amber' | 'emerald' | 'slate' | 'red' | 'rose';

const TONES: Record<AlertTone, string> = {
  blue: 'bg-blue-50 text-blue-800 ring-blue-200',
  amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  emerald: 'bg-emerald-50 text-emerald-800 ring-emerald-200',
  slate: 'bg-slate-50 text-slate-700 ring-slate-200',
  red: 'bg-red-50 text-red-800 ring-red-200',
  rose: 'bg-rose-50 text-rose-800 ring-rose-200',
};

/**
 * The glyph's own disc, a step deeper than the banner's ground.
 *
 * Product call, 29 Sep 2026: *use this banner template for profile pages that have suggest
 * actions.* The glyph was drawn bare, so on a tinted strip it read as a mark somebody had left
 * beside the text rather than as the banner's own icon — the disc is what makes it the subject.
 *
 * ONE step, never two: at `-200` it would match the ring and the banner would read as a box
 * with a second box in it. `-100` sits between the ground and the edge, which is the same
 * three-surface reasoning the folder tabs run on.
 */
const DISCS: Record<AlertTone, string> = {
  blue: 'bg-blue-100',
  amber: 'bg-amber-100',
  emerald: 'bg-emerald-100',
  slate: 'bg-slate-200',
  red: 'bg-red-100',
  rose: 'bg-rose-100',
};

/**
 * Persistent profile status banner — reference §7 and §14.
 *
 * Rendered for **every** lifecycle state, never `return null`. A page that hides the banner
 * when nothing is actionable teaches the user to ignore the region entirely; "waiting" and
 * "finished" are states worth stating.
 *
 * Tones: blue in-progress · emerald good-to-go · amber attention · slate neutral-terminal ·
 * red rejected/blocked.
 */
export function NextActionAlert({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  action,
  permission,
  actionPermission,
  actionDisabledReason = null,
  tone = 'blue',
  onDismiss,
  id,
}: {
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** The default CTA renders only when BOTH label and handler are set. */
  actionLabel?: string;
  onAction?: () => void;
  /** A custom CTA, which overrides the default button. */
  action?: ReactNode;
  /**
   * Gates the WHOLE banner. Usually the wrong choice — prefer `actionPermission`, so a
   * read-only viewer can still see what state the record is in.
   */
  permission?: string;
  /** Gates just the default CTA. The banner and its state stay visible to everyone. */
  actionPermission?: string;
  /**
   * DISABLES the default CTA for STATE, saying why in a `<Tooltip>` — the permission HIDES, this
   * disables. `<ProfileCard>` passes its `editDisabledReason`, so a banner offering the card's Edit
   * cannot open a form the card's own cog refuses.
   */
  actionDisabledReason?: string | null;
  tone?: AlertTone;
  onDismiss?: () => void;
  id?: string;
}) {
  const body = (
    <div
      id={id}
      role="status"
      className={`flex items-start gap-3 rounded-panel px-4 py-3 ring-1 ${TONES[tone]}`}
    >
      {/*
        THE GLYPH IN A DISC — 36px, centred, the tone's own step. `shrink-0` because the banner
        is a flex row and the description beside it is the part that wraps; without it a long
        sentence squeezes the disc into an oval.

        `slate-200` rather than `slate-100` on the neutral tone: the banner's ground IS
        `slate-50`, so one step from it is 100 and that is invisible. The other five sit on a
        `-50` ground and take `-100`.
      */}
      <span
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${DISCS[tone]}`}
        aria-hidden="true"
      >
        {icon}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        {description ? <p className="mt-0.5 text-xs opacity-90">{description}</p> : null}
      </div>

      {action ?? (actionLabel && onAction ? (
        <PermissionGate permission={actionPermission}>
          {/* Wrapped only when there is something to say — the other 27 callers keep their bare button. */}
          {actionDisabledReason != null ? (
            <Tooltip disabledReason={actionDisabledReason}>
              <button
                type="button"
                id={id ? `${id}-action` : undefined}
                disabled
                className="shrink-0 rounded-lg bg-slate-200 px-3 py-1.5 text-xs font-medium text-slate-400 ring-1 ring-slate-200"
              >
                {actionLabel}
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              id={id ? `${id}-action` : undefined}
              onClick={onAction}
              className="shrink-0 rounded-lg bg-white/80 px-3 py-1.5 text-xs font-medium ring-1 ring-current/20 transition hover:bg-white"
            >
              {actionLabel}
            </button>
          )}
        </PermissionGate>
      ) : null)}

      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded p-1 opacity-60 transition hover:opacity-100"
        >
          ✕
        </button>
      ) : null}
    </div>
  );

  return permission ? <PermissionGate permission={permission}>{body}</PermissionGate> : body;
}
